import { HTMLAttributes, useEffect, useCallback, useRef ,useState} from "react";
import TechniqueFallback from "@/assets/technique.png";
import { installFlowTechniquePhoto } from "@/installFlow/storage-keys";
import { Button } from "../ui/button";
import { CommonFlowFrame } from "./common-flow-frame";
import { useNavigate } from "@tanstack/react-router";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";

interface AuthProps extends HTMLAttributes<HTMLDivElement> {}

export function ReviewNamePlatePhoto({ className }: AuthProps) {
  const [Technique, setTechnique] = useState<string>(TechniqueFallback);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);  
  const navigate = useNavigate();
  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  function goToNextStep() {
    navigate({ to: "/installFlow/review-name-plate-info" });
  }

  useEffect(() => {
    if (typeof sessionStorage === "undefined") return;
    const stored = installFlowTechniquePhoto.get();
    if (stored) setTechnique(stored);
  }, []);
  async function openCamera() {
    setCameraError(null);
    setCameraOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;
      await new Promise<void>((resolve, reject) => {
        if (!videoRef.current) {
          reject(new Error("Video element not ready"));
          return;
        }
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play().then(resolve).catch(reject);
        };
        videoRef.current.onerror = () =>
          reject(new Error("Video failed to load"));
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not access camera";
      setCameraError(
        message.includes("Permission") || message.includes("NotAllowed")
          ? "Camera access was denied. Please allow camera in your browser settings."
          : "Could not access camera. Please check permissions and try again.",
      );
      setCameraOpen(false);
    }
  }
  function handleCloseCamera(open: boolean) {
    if (!open) {
      stopStream();
      setCameraError(null);
      setCameraOpen(false);
    }
  }
  function capturePhoto() {
    const video = videoRef.current;
    if (!video || !streamRef.current || isCapturing) return;
    setIsCapturing(true);
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      setIsCapturing(false);
      return;
    }
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    if (typeof sessionStorage !== "undefined") {
      installFlowTechniquePhoto.set(dataUrl);
    }
    setTechnique(dataUrl);
    stopStream();
    setCameraOpen(false);
    setIsCapturing(false);
  }
  return (
    <CommonFlowFrame>
      <div className="w-full max-w-[460px] h-full flex flex-col items-center justify-start">
        <div className="flex flex-col gap-6 items-center justify-start h-1/2">
          <img
            src={Technique}
            alt="Technique"
            className="w-full h-[330px] object-cover"
          />
          <div className="text-left px-6 gap-4 flex flex-col">
            <h2 className="text-xl font-bold text-black leading-[26px]">
              How does this look?
            </h2>
            <p className="text-base text-[#727272] leading-[26px] font-semibold">
              Make sure the entire nameplate is in the photo and that text is
              legible.
            </p>
          </div>
        </div>
        <div className="w-full flex flex-col px-6 h-[40%] items-end justify-start">
          <div className="h-[100px] flex gap-4 w-full items-center justify-center">
            <Button
              variant="secondary"
              className="w-1/2 h-[42px] p-4 items-center justify-center rounded-[25px]"
              onClick={openCamera}
            >
              Retake
            </Button>
            <Button
              variant="installFlow"
              className="w-1/2 h-[42px] p-4 items-center justify-center"
              onClick={goToNextStep}
            >
              Looks Good
            </Button>
          </div>
        </div>
      </div>
      <Dialog open={cameraOpen} onOpenChange={handleCloseCamera}>
        <DialogContent
          className="sm:max-w-[500px]"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Take nameplate photo</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="relative w-full aspect-[4/3] bg-black rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
            </div>
            {cameraError && (
              <p className="text-sm text-destructive">{cameraError}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={capturePhoto}
              disabled={isCapturing || !!cameraError}
              className="w-full bg-[#0053EE] text-white"
            >
              {isCapturing ? "Capturing…" : "Capture photo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CommonFlowFrame>
  );
}
